import { useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Divider,
    Switch,
    FormControlLabel,
    Grid2 as Grid
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { CommonCard } from '../../component/cards/MainCard2';
import { SubmitButton } from '../../component/buttons';
import ManagementHeader from './componets/ManagementHeader';
import { postClinic, postClinicPicture } from 'services/clinics';

const CreateClinic = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const DEFAULT_FEATURES = [
        { key: 'REHAB', label: t('clinics.featuresNames.rehab') },
        { key: 'TRAIN', label: t('clinics.featuresNames.trainingPlans') },
        { key: 'NUTRITION', label: t('clinics.featuresNames.nutritionPlans') },
        { key: 'QUESTIONNAIRES', label: t('clinics.featuresNames.questionnaires') },
        { key: 'CHAT', label: t('clinics.featuresNames.chat') },
        { key: 'LEARNING', label: t('clinics.featuresNames.learning') },
        { key: 'TELECONSULT', label: t('clinics.featuresNames.teleconsultations') }
    ];

    const [form, setForm] = useState({
        name: '',
        email: '',
        plan: '',
        addr_text: '',
        addr_zip: '',
        addr_city: '',
        addr_province: '',
        addr_country: '',
        features: []
    });

    const handleChange = (field) => (e) => {
        setForm((prev) => ({
            ...prev,
            [field]: e.target.value
        }));
    };

    const toggleFeature = (feature) => {
        setForm((prev) => ({
            ...prev,
            features: prev.features.includes(feature)
                ? prev.features.filter((f) => f !== feature)
                : [...prev.features, feature]
        }));
    };

    const handleLogoUpload = (event) => {
        const file = event.target.files[0];
        if (!file) return;
        setForm((prev) => ({
            ...prev,
            logo_file: file,
            logo_url: URL.createObjectURL(file)
        }));
    };

    const isValid = form.name && form.addr_country;

    const handleSubmit = async () => {
        try {
            const payload = {
                name: form.name,
                email: form.email || null,
                plan: form.plan || null,
                features: form.features.length ? form.features : null,
                address: {
                    street: form.addr_text || null,
                    zip: form.addr_zip || null,
                    city: form.addr_city || null,
                    province: form.addr_province || null,
                    country: form.addr_country || null
                }
            };

            const response = await postClinic(payload);
            const clinic = response.data;

            if (form.logo_file) {
                await postClinicPicture(clinic.uuid, form.logo_file);
            }

            navigate('/mgmt/clinics');
        } catch (err) {
            console.error('Failed to create clinic', err);
            alert(t('clinics.createError'));
        }
    };

    return (
        <CommonCard sx={{ m: 2, p: 2 }}>
            <ManagementHeader
                title={t('clinics.createNew')}
                subtitle={t('clinics.createSubtitle')}
                backTo="/mgmt/clinics"
            />

            {/* General */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        {t('clinics.tabs.general')}
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                label={t('clinics.name')}
                                fullWidth
                                value={form.name}
                                onChange={handleChange('name')}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                label={t('clinics.email')}
                                fullWidth
                                value={form.email}
                                onChange={handleChange('email')}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                label={t('clinics.plan')}
                                fullWidth
                                value={form.plan}
                                disabled
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Address */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        {t('clinics.tabs.address')}
                    </Typography>

                    <Grid container spacing={2}>
                        <Grid size={12}>
                            <TextField
                                label={t('clinics.address')}
                                fullWidth
                                value={form.addr_text}
                                onChange={handleChange('addr_text')}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField
                                label={t('clinics.zip')}
                                fullWidth
                                value={form.addr_zip}
                                onChange={handleChange('addr_zip')}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField
                                label={t('clinics.city')}
                                fullWidth
                                value={form.addr_city}
                                onChange={handleChange('addr_city')}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField
                                label={t('clinics.province')}
                                fullWidth
                                value={form.addr_province}
                                onChange={handleChange('addr_province')}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField
                                label={t('clinics.country')}
                                fullWidth
                                value={form.addr_country}
                                onChange={handleChange('addr_country')}
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Features */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        {t('clinics.tabs.features')}
                    </Typography>

                    <Divider sx={{ mb: 2 }} />

                    <Grid container spacing={2}>
                        {DEFAULT_FEATURES.map((feature) => (
                            <Grid size={{ xs: 12, md: 6 }} key={feature.key}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={form.features.includes(feature.key)}
                                            onChange={() => toggleFeature(feature.key)}
                                        />
                                    }
                                    label={feature.label}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </CardContent>
            </Card>

            {/* Branding */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        {t('clinics.tabs.branding')}
                    </Typography>

                    <Grid container spacing={3}>
                        <Grid size={12}>
                            <Box
                                sx={{
                                    width: 200,
                                    height: 200,
                                    border: '1px dashed',
                                    borderColor: 'divider',
                                    borderRadius: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: '#fff'
                                }}
                            >
                                {form.logo_url ? (
                                    <img
                                        src={form.logo_url}
                                        alt="Clinic logo"
                                        style={{
                                            maxWidth: '100%',
                                            maxHeight: '100%',
                                            objectFit: 'contain'
                                        }}
                                    />
                                ) : (
                                    <Typography variant="caption" color="text.secondary">
                                        {t('clinics.noLogo')}
                                    </Typography>
                                )}
                            </Box>
                        </Grid>

                        <Grid size={12}>
                            <SubmitButton component="label">
                                {t('clinics.uploadLogo')}
                                <input
                                    type="file"
                                    hidden
                                    accept="image/png, image/jpeg, image/svg+xml"
                                    onChange={handleLogoUpload}
                                />
                            </SubmitButton>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Actions */}
            <Box display="flex" justifyContent="flex-end">
                <SubmitButton disabled={!isValid} onClick={handleSubmit}>
                    {t('common.create')}
                </SubmitButton>
            </Box>
        </CommonCard>
    );
};

export default CreateClinic;
